import React, { useState, useRef, useEffect } from 'react';
import ApiService from '../../services/api-service';
import MemorySection from './MemorySection';
import CalendarSection from './CalendarSection';
import ReactMarkdown from 'react-markdown';
import './ChatWithAI.scss';
import SikibotImg from '../../assets/common/resources/Sikibot.png';
import { useDispatch } from 'react-redux';
import { addSongAction } from '../../action/songs';

const DEFAULT_USER_PHOTO = '/assets/default-user.png';
const SIKIBOT_PHOTO = SikibotImg;

function ChatWithAI() {
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userProfilePhoto, setUserProfilePhoto] = useState(DEFAULT_USER_PHOTO);
  const [sidebarTab, setSidebarTab] = useState('memory');
  const [userId, setUserId] = useState(null);
  const [lastMusicResults, setLastMusicResults] = useState(null);
  const chatEndRef = useRef(null);
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const res = await ApiService.USER_PROFILE();
        setUserProfilePhoto(res?.result?.profileImg?.filePath || DEFAULT_USER_PHOTO);
        setUserId(res?.result?._id);
      } catch (e) {
        setUserProfilePhoto(DEFAULT_USER_PHOTO);
        setUserId(null);
      }
    };
    fetchUserProfile();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle playing music from search results - simplified like SidebarRight
  const handlePlaySearchResult = async (result) => {
    try {
      // Simply dispatch the song like SidebarRight does
      dispatch(addSongAction(result));
      
      // Add confirmation message
      setMessages(msgs => [...msgs, {
        sender: 'ai',
        text: `🎵 Now playing **${result.title}** by ${result.artist}`
      }]);
    } catch (error) {
      console.error('Error playing song:', error);
      setMessages(msgs => [...msgs, {
        sender: 'ai',
        text: `Sorry, I couldn't play that song. Please try again.`
      }]);
    }
  };

  // Check for numbered selections from music results
  const processUserInput = (input) => {
    const match = input.match(/^(\d+)$/);
    if (match && lastMusicResults) {
      const index = parseInt(match[1]) - 1;
      if (index >= 0 && index < lastMusicResults.length) {
        const selected = lastMusicResults[index];
        handlePlaySearchResult(selected);
        return true;
      }
    }
    return false;
  };

  const askAI = async (customQuestion, options = {}) => {
    const q = (typeof customQuestion === 'string' ? customQuestion : question).trim();
    if (!q) return;

    // Check if it's a numbered selection
    if (processUserInput(q)) {
      setQuestion('');
      return;
    }

    const userMsg = { sender: 'user', text: q, userProfilePhoto };
    
    setMessages((msgs) => [...msgs, userMsg]);
    setLoading(true);
    setQuestion('');

    try {
      // Use the enhanced AI method that includes memory context
      const res = await ApiService.AI_ENHANCED_ASK(userMsg.text, {
        userId, // Pass user ID for context
        ...options
      });
      
      console.log('AI RAW RESPONSE:', res);
      
      const answer = res?.answer || 'No response';
      const image = res?.image_url;
      const usedMemories = res?.memories || [];
      const musicPlaying = res?.musicPlaying;
      const musicData = res?.musicData;
      const searchResults = res?.searchResults;
      
      // Store search results for numbered selection
      if (searchResults && searchResults.length > 0) {
        setLastMusicResults(searchResults);
      }
      
      // If music should play immediately - simplified like SidebarRight
      if (musicPlaying && musicData) {
        try {
          // Use the same simple dispatch pattern as SidebarRight
          dispatch(addSongAction(musicData));
        } catch (error) {
          console.error('Error auto-playing music:', error);
        }
      }
      
      const aiMsg = { 
        sender: 'ai', 
        text: answer, 
        image,
        usedMemories: usedMemories.length > 0 ? usedMemories : null,
        searchResults
      };
      
      console.log('AI MSG:', aiMsg);
      setMessages((msgs) => [...msgs, aiMsg]);
    } catch (e) {
      // Check if it's a premium requirement error
      if (e.response?.data?.code === 'PREMIUM_REQUIRED') {
        setMessages((msgs) => [
          ...msgs,
          { 
            sender: 'ai', 
            text: 'Siki Bot is a Premium feature! 🌟\n\nSubscribe to Premium to unlock:\n• Unlimited AI chat conversations\n• 750 monthly streams\n• Exclusive content access\n• And much more!\n\nClick the Upgrade button in the sidebar to get started!',
            isPremiumRequired: true
          },
        ]);
      } else {
        setMessages((msgs) => [
          ...msgs,
          { sender: 'ai', text: e.message || 'No answer' },
        ]);
      }
    }
    setLoading(false);
  };
  
  // Function to save a message to memory
  const saveToMemory = async (text) => {
    if (!userId || !text) return;
    
    try {
      setLoading(true);
      await ApiService.AI_ADD_MEMORY(text, {
        importance: 8,
        source: 'conversation'
      });
      // Update the memory tab to show the new memory
      if (sidebarTab !== 'memory') {
        setSidebarTab('memory');
      }
    } catch (e) {
      console.error('Failed to save to memory:', e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ai-chat-main-flex">
      {/* Chat Section */}
      <div className="ai-chat-container">
        <div className="ai-chat-history">
          {messages.map((msg, idx) => (
            <div key={idx} className={`ai-chat-bubble ${msg.sender === 'user' ? 'user' : 'ai'}`}>
              <span className="ai-chat-avatar">
                <img
                  src={msg.sender === 'user' ? msg.userProfilePhoto || DEFAULT_USER_PHOTO : SIKIBOT_PHOTO}
                  alt={msg.sender === 'user' ? 'User' : 'SikiBot'}
                  className="ai-avatar-img"
                />
              </span>
              <div className="ai-chat-message">
                <div className="ai-chat-text">
                  {msg.sender === 'ai' ? (
                    <>
                      <ReactMarkdown
                        components={{
                          // Custom styling for markdown elements
                          p: ({ children }) => <p style={{ margin: '0 0 8px 0' }}>{children}</p>,
                          ul: ({ children }) => <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>{children}</ul>,
                          li: ({ children }) => <li style={{ marginBottom: '4px' }}>{children}</li>,
                          strong: ({ children }) => <strong style={{ color: 'var(--primary-color, #FFD700)' }}>{children}</strong>,
                          code: ({ inline, children }) => 
                            inline ? (
                              <code style={{ 
                                backgroundColor: 'var(--background-color, #1a1a1a)', 
                                padding: '2px 4px', 
                                borderRadius: '3px',
                                color: 'var(--primary-color, #FFD700)'
                              }}>{children}</code>
                            ) : (
                              <code style={{ 
                                display: 'block',
                                backgroundColor: 'var(--background-color, #1a1a1a)', 
                                padding: '8px', 
                                borderRadius: '4px',
                                overflowX: 'auto',
                                marginBottom: '8px'
                              }}>{children}</code>
                            ),
                          a: ({ children, href }) => (
                            <a href={href} target="_blank" rel="noopener noreferrer" 
                               style={{ color: 'var(--primary-color, #FFD700)', textDecoration: 'underline' }}>
                              {children}
                            </a>
                          ),
                        }}
                      >
                        {msg.text}
                      </ReactMarkdown>

                      {/* Music Search Results - Clickable */}
                      {msg.searchResults && msg.searchResults.length > 0 && (
                        <div className="music-search-results">
                          {msg.searchResults.map((result, idx) => (
                            <button
                              key={idx}
                              className="music-result-item"
                              onClick={() => handlePlaySearchResult(result)}
                            >
                              <span className="result-number">{idx + 1}</span>
                              {result.thumbnailUrl ? (
                                <img 
                                  src={result.thumbnailUrl} 
                                  alt={result.title}
                                  className="result-thumbnail"
                                />
                              ) : (
                                <div className="result-thumbnail-placeholder">
                                  🎵
                                </div>
                              )}
                              <div className="result-info">
                                <span className="result-title">{result.title}</span>
                                <span className="result-artist">{result.artist}</span>
                              </div>
                              <span className="result-duration">{result.duration}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    msg.text
                  )}
                  
                  {msg.image && (
                    <img
                      src={msg.image}
                      alt="AI generated"
                      style={{ maxWidth: 220, display: 'block', marginTop: 8, borderRadius: 8 }}
                    />
                  )}
                </div>
                
                {/* Display memory indicator and save button */}
                <div className="ai-chat-actions">
                  {msg.sender === 'user' && (
                    <button 
                      className="ai-remember-btn"
                      onClick={() => saveToMemory(msg.text)}
                      title="Save to Memory"
                    >
                      Remember this
                    </button>
                  )}
                  
                  {msg.usedMemories && (
                    <div className="ai-memory-indicator">
                      <span className="ai-memory-icon" title="Used memories to generate this response">
                        💭
                      </span>
                      <small>Using {msg.usedMemories.length} memories</small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          {loading && (
            <div className="ai-chat-bubble ai">
              <span className="ai-chat-avatar">
                <img src={SIKIBOT_PHOTO} alt="SikiBot" className="ai-avatar-img" />
              </span>
              <span className="ai-chat-loading">Thinking...</span>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>
        <div className="ai-chat-input-bar">
          <input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Type your message... (or type a number to select from music results)"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && question.trim()) {
                askAI();
              }
            }}
          />
          <button onClick={() => askAI()} disabled={loading || !question.trim()}>
            Send
          </button>
        </div>
      </div>

      {/* Sidebar Section */}
      <div className="ai-chat-sidebar">
        <div className="ai-chat-sidebar-tabs">
          <button
            className={sidebarTab === 'memory' ? 'active' : ''}
            onClick={() => setSidebarTab('memory')}
          >
            Memory
          </button>
          <button
            className={sidebarTab === 'calendar' ? 'active' : ''}
            onClick={() => setSidebarTab('calendar')}
          >
            Calendar
          </button>
        </div>
        <div className="ai-chat-sidebar-content">
          {sidebarTab === 'memory' && (
            <MemorySection userId={userId} loading={loading} setLoading={setLoading} />
          )}
          {sidebarTab === 'calendar' && (
            <CalendarSection userId={userId} loading={loading} setLoading={setLoading} />
          )}
        </div>
      </div>
    </div>
  );
}

export default ChatWithAI;